import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { type EventHandler, eventEmitter } from "@/eventEmitter";
import { ExportIcon } from "@/icons";
import { BottomBarItem } from "@/interface/BottomBar/BottomRightBar";
import { Tooltip } from "@/interface/ui/Tooltip";
import { HStack } from "@/shared/ui/Stack";
import { ExportMenu } from "./ExportMenu";
import styles from "./ExportMenu.module.css";
import type { ExportButtonProps, ExportMenuFormat } from "./types";

export const ExportButton = ({ singletoneTarget, isActive, isDisabled }: ExportButtonProps) => {
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLElement>(null);
    const menuRef = useRef<HTMLElement>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const closeMenu = useCallback((resetActive = true) => {
        setIsMenuOpen(false);
        if (resetActive) {
            eventEmitter.dispatch("header.button.active.name", {
                name: null,
            });
        }
    }, []);

    const openExportMenu = useCallback(() => {
        canv.appInterface.imageExportWindow.close();
        canv.appInterface.exportWindow.close();

        setIsMenuOpen(true);
        eventEmitter.dispatch("header.button.active.name", {
            name: "export",
        });
    }, []);

    useEffect(() => {
        const handleActiveButtonChange: EventHandler<"header.button.active.name"> = ({ name }) => {
            if (name !== "export") {
                setIsMenuOpen(false);
            }
        };

        eventEmitter.on("header.button.active.name", handleActiveButtonChange);
        return () => eventEmitter.off("header.button.active.name", handleActiveButtonChange);
    }, []);

    useEffect(() => {
        const handleOpenExportMenu: EventHandler<"export.menu.open"> = () => {
            openExportMenu();
        };

        eventEmitter.on("export.menu.open", handleOpenExportMenu);
        return () => eventEmitter.off("export.menu.open", handleOpenExportMenu);
    }, [openExportMenu]);

    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }

        const handleOutsidePointerDown = (event: PointerEvent) => {
            if (event.button !== 0) {
                return;
            }

            const wrapper = wrapperRef.current;
            if (!wrapper || !(event.target instanceof Node)) {
                return;
            }

            if (wrapper.contains(event.target)) {
                return;
            }

            if (menuRef.current?.contains(event.target)) {
                return;
            }

            closeMenu();
        };

        document.addEventListener("pointerdown", handleOutsidePointerDown);
        return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
    }, [closeMenu, isMenuOpen]);

    const toggleExportMenu = () => {
        if (canv.appInterface.imageExportWindow.opened) {
            closeMenu(false);
            canv.appInterface.imageExportWindow.close();
            return;
        }

        if (isMenuOpen) {
            closeMenu();
            return;
        }

        openExportMenu();
    };

    const onExport = (format: ExportMenuFormat) => {
        closeMenu(false);

        if (format === "pdf") {
            canv.appInterface.exportWindow.open();
            return;
        }

        canv.appInterface.imageExportWindow.open(format);
    };

    return (
        <HStack className={styles.exportButtonWrapper} ref={wrapperRef}>
            <Tooltip singleton={singletoneTarget} content={t("export.title")}>
                <BottomBarItem
                    className="barPanelExport stroke"
                    active={isActive || isMenuOpen}
                    disabled={isDisabled}
                    onClick={toggleExportMenu}
                >
                    <ExportIcon />
                </BottomBarItem>
            </Tooltip>
            {isMenuOpen && (
                <ExportMenu anchorRef={wrapperRef} menuRef={menuRef} onExport={onExport} />
            )}
        </HStack>
    );
};
